import { Controller } from '@hotwired/stimulus';
const placesApiPromise =
  process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'development'
    ? import('../services/places_mock.js')
    : import('../services/places.js');

// Connects to data-controller="googleplaces"
export default class extends Controller {
  static targets = [
    'input',
    'street',
    'houseNo',
    'sublocalityLevel2',
    'sublocalityLevel1',
    'zip',
    'city',
    'administrativeAreaLevel1',
    'country',
    'latitude',
    'longitude',
    'viewport',
  ];
  static values = {
    apikey: String,
    types: Array,
    fields: Array,
  };

  connect() {
    placesApiPromise.then(
      (placesApi) =>
        (this.placesApi = new placesApi.default(
          this.apikeyValue,
          this.inputTarget,
          this.onPlaceChange.bind(this),
          this.hasTypesValue ? this.typesValue : undefined,
          this.hasFieldsValue ? this.fieldsValue : undefined,
        )),
    );
  }

  disconnect() {
    this.placesApi.disconnect();
  }

  onPlaceChange() {
    this.setDetailsToDefault();

    const place = this.placesApi.getPlace();

    if (place.address_components) {
      const componentTypeToTarget = {
        sublocality_level_1: this.sublocalityLevel1Targets[0],
        sublocality_level_2: this.sublocalityLevel2Targets[0],
        postal_code: this.zipTargets[0],
        locality: this.cityTargets[0],
        administrative_area_level_1: this.administrativeAreaLevel1Targets[0],
        country: this.countryTargets[0],
        route: this.streetTargets[0],
        street_number: this.houseNoTargets[0],
      };
      const componentTypes = Object.keys(componentTypeToTarget);

      for (const component of place.address_components) {
        const componentType = component.types.find((type) =>
          componentTypes.includes(type),
        );
        const target = componentTypeToTarget[componentType];

        if (target !== undefined) {
          // We need to handle postal codes specially. Sometimes (when one enters a large city
          // into a location input) Google only gives us the first two digits of the postal code.
          // We don't want to use that, since it's not a valid postal code, so users might get
          // strange errors -- for example, we have forms where a missing postal code is not an
          // error, but a two-digit postal code is; that's confusing.
          // Luckily, Google actually marks these prefixes as prefixes, so they're easy to ignore.
          if (
            !(
              componentType == 'postal_code' &&
              component.types.includes('postal_code_prefix')
            )
          ) {
            target.value = component.long_name;
          }
        }
      }
    }

    if (place.geometry) {
      if (this.hasLatitudeTarget) {
        this.latitudeTarget.value = place.geometry.location.lat();
      }
      if (this.hasLongitudeTarget) {
        this.longitudeTarget.value = place.geometry.location.lng();
      }

      if (this.hasViewportTarget && place.geometry.viewport) {
        this.viewportTarget.value = JSON.stringify(place.geometry.viewport);
      }
    }

    const mapBoundsInput = document.querySelector('input[name=map_bounds]');

    if (mapBoundsInput) {
      mapBoundsInput.value = '';
    }

    this.dispatch('changed');
  }

  selectFirstSuggestion(event) {
    if (document.querySelector('.pac-item')) {
      if (!document.querySelector('.pac-item-selected')) {
        event.target.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'ArrowDown',
            code: 'ArrowDown',
            keyCode: 40,
          }),
        );
      }

      event.preventDefault();
    }
  }

  setDetailsToDefault() {
    for (const targetName of this.constructor.targets) {
      if (targetName === 'input') {
        continue;
      }

      const target = this[`${targetName}Targets`][0];
      if (target) {
        target.value = '';
      }
    }
  }
}
